import * as React from "react";

import { AddVariantDialog } from "@/components/catalog/add-variant-dialog";
import { CatalogSearchBox } from "@/components/catalog/catalog-search-box";
import { ItemSearchFilters } from "@/components/catalog/item-search-filters";
import {
  ItemDetailDialog,
  type ItemDialogCta,
  type ItemDialogSeed,
  type ItemDialogTarget,
} from "@/components/catalog/item-detail-dialog";
import { DELETE_ITEM_ERROR } from "@/components/catalog/item-detail-page";
import { ItemCardGrid } from "@/components/catalog/item-card-grid";
import type { CardOpening, ItemSearchCard } from "@/components/catalog/item-search-card-face";
import {
  IslandSkeleton,
  ItemCardSkeleton,
  ItemGridSkeleton,
} from "@/components/catalog/item-search-skeletons";
import { RequestAddItem } from "@/components/catalog/request-add-item";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import { EmptyTable } from "@/components/ui/empty-table";
import { ErrorSnackbar } from "@/components/ui/error-snackbar";
import { MuiAlert } from "@/components/ui/mui-alert";
import {
  useCatalogCategoryTree,
  useCatalogItemDetail,
  useDeleteCatalogItem,
} from "@/hooks/catalog-items";
import { useCatalogTiers } from "@/hooks/catalog-admin";
import { announceItemDeleted, useItemsError } from "@/hooks/items-error";
import {
  useIslandHeldItems,
  useIslandManufacturerNames,
  useIslandItem,
  useItemSearchIsland,
  type IslandItem,
  type IslandVariantGroups,
} from "@/hooks/item-search-island";
import { useRememberIslandManufacturer } from "@/hooks/item-search-island-manufacturers";
import { usePermissions } from "@/hooks/permissions";
import { MUI_UP_LG_QUERY, useMatchesMedia } from "@/hooks/use-matches-media";
import type { SpecEditorTarget } from "@/hooks/spec-editor-target";
import {
  selectionItemPreview,
  useClearSpecSelectionItem,
  useSelectSpecSelectionItem,
} from "@/hooks/spec-editor-selections";
import type { SpecLinesLine, SpecLinesSelection } from "@/hooks/specifications";
import type { CatalogListItem } from "@/lib/catalog-item";
import {
  categoryWrite,
  islandActiveSelection,
  islandAlert,
  islandGates,
  islandGridItems,
  itemCategorySlug,
  searchSettled,
  shouldClearEmptyCategoryFilter,
  type IslandSearch,
  type IslandSearchPatch,
} from "@/lib/item-search-island";
import { ERRORS } from "@/lib/spec-editor-errors";
import { resolveActiveRow, type ActiveRowIds } from "@/lib/spec-lines-display";
import { lennarPriceCategoryIds } from "@/lib/target-cost-display";

// The Item Search island host (#811) — the donor's `ItemSearchIsland`
// (`timmy/src/domains/items/islands/ItemSearchIsland/ItemSearchIsland.tsx`)
// and its observers, in the edit page's right pane (#806's `rightPane`). It
// reads the page's Active selection and composes #798's faces over the
// catalog's list read; the editor's left pane is none of its business.
//
// **What it reads from the page**: the loaded lines and the Active selection's
// ids (`CONTEXT.md`, Active selection), resolved into the donor's public
// payload (`islandActiveSelection`). Each *set* of the Active selection — a
// row click, the same row's included, is a new ids object — is the donor's
// emit: it re-runs the category write and re-opens a dismissed alert.
//
// **The URL is the island's state** beside the editor's `specSearch`:
// `itemSearch`, `category`, `mfrs`, `tiers` (and `status`, read only by the
// self-heal), each written with `replace` and merged into the rest.
//
// **The category write** (`observers.ts:12-60`): on each emit, and when the
// row's served slug moves, the slug is written only if it differs from the
// URL's — set, or deleted for an empty one. Nothing is written before the
// first emit, so a deep-linked `?category=` survives arrival. A `Select` on
// the Active selection writes the picked item's category at the click, as the
// donor's re-emit does before its write lands (#811 Assumption 8). A Remove
// Selected on it writes the selection's category once its write lands, the
// donor's re-emit after the remove (#964 N14).
//
// **The self-heal**: a category that is the search's only narrowing and
// returns nothing, once settled, is deleted (`catalogCategoryFallback.ts`).
//
// **The grid**: the pinned card (the Active selection's item, read alone when
// the island holds no copy, its skeleton in slot 1), the results less its id,
// the last requested item (#798's gate), the new-query skeletons and the
// paging sentinel; below it the Request Item band on every settled search and
// `No results` only on a failed one.
//
// **One handler per click** for Select and Remove Selected, shared by the
// cards and the item dialog's CTA mode (#767 R1's repair of the donor's
// doubled listener), through #801's verbs; each failure on its own
// `ErrorSnackbar` (#811 Assumption 16).

/** The item dialog, as the page holds it: what it opened on, and its target. */
export interface IslandItemDialog {
  seed: ItemDialogSeed;
  /** The Active selection when it opened — fixed for its life (#811 Assumption 9). */
  target: ActiveRowIds | null;
  /** Whether its close hands the focus back to its opener; absent, it does. */
  restoreFocus?: boolean;
}

interface ItemSearchIslandProps {
  /** The lines the page holds — the Active selection resolves over them. */
  lines: readonly SpecLinesLine[];
  /** The page's Active selection, as set (its identity is the emit). */
  activeRow: ActiveRowIds | null;
  /** Sets the page's Active selection — the island's own emit, at a Select. */
  onActiveRowChange: (row: ActiveRowIds) => void;
  /** The island's URL keys. */
  search: IslandSearch;
  /** Merge these keys into the URL, replacing the entry; `undefined` removes one. */
  onSearchChange: (patch: IslandSearchPatch) => void;
  target: SpecEditorTarget;
  itemDialog: IslandItemDialog | null;
  onItemDialogChange: (next: IslandItemDialog | null) => void;
  /**
   * The editor's target-cost switch, which the cards' Max Price chips follow
   * (#1216). A prop rather than the donor's `targetCostChangedEvent` listener
   * (`observers.ts:135-162`), so a card can never miss it — the listener
   * mounted after the editor's broadcast and showed every chip (PB-T7).
   */
  showMaxPrice: boolean;
}

const ITEM_SEARCH_ERROR = "Error loading item search results. Please refresh page to try again.";
const ITEM_METADATA_ERROR =
  "Error loading item search filter options. Please refresh page to try again.";

/** The island's column ramp (`Layout.tsx:97-103`), breakdown ruling 3. */
const ISLAND_COLUMNS = { xs: 1, sm: 2, md: 3, lg: 2, xl: 3 } as const;

export function ItemSearchIsland({
  lines,
  activeRow,
  onActiveRowChange,
  search,
  onSearchChange,
  target,
  itemDialog,
  onItemDialogChange,
  showMaxPrice,
}: ItemSearchIslandProps) {
  const permissions = usePermissions();
  const resolved = resolveActiveRow<SpecLinesSelection, SpecLinesLine>(lines, activeRow);
  const active = islandActiveSelection(resolved, { itemCleared: activeRow?.itemCleared });
  const gates = islandGates(active);

  // --- The URL ------------------------------------------------------------
  const onSearchChangeRef = React.useRef(onSearchChange);
  React.useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  });
  const urlCategory = search.category ?? "";
  const currentCategory = React.useRef(urlCategory);
  React.useEffect(() => {
    currentCategory.current = urlCategory;
  }, [urlCategory]);
  const writeCategory = React.useCallback((slug: string) => {
    const write = categoryWrite(currentCategory.current, slug);
    if (!write) return;
    currentCategory.current = slug;
    onSearchChangeRef.current(write);
  }, []);
  const emitted = React.useRef(false);
  const rowResolved = resolved !== null;
  // The slug is read at the emit, never watched: the donor writes the category
  // when a row is made active (and at Select), so a selection whose category
  // later changes under it — a Select landing or rolled back — leaves the URL
  // as it is (`SpecEditIsland/data/observers.ts:178-209`).
  const activeSlug = React.useRef(active.categorySlug);
  React.useEffect(() => {
    activeSlug.current = active.categorySlug;
  });
  // Once per activation: a row that leaves the loaded lines and comes back is
  // not made active again.
  const emittedFor = React.useRef<ActiveRowIds | null>(null);
  React.useEffect(() => {
    if (activeRow === null && !emitted.current) return;
    // A row the cache no longer holds (a search page without it) emits nothing.
    if (activeRow !== null && !rowResolved) return;
    if (emitted.current && emittedFor.current === activeRow) return;
    emitted.current = true;
    emittedFor.current = activeRow;
    writeCategory(activeSlug.current);
  }, [activeRow, rowResolved, writeCategory]);

  // --- The search ---------------------------------------------------------
  const results = useItemSearchIsland(search);
  const settled = searchSettled(results);
  const tree = useCatalogCategoryTree();
  // Falcon's `appliancesCategories`, from the tree the island already reads.
  const lennarPriceIds = React.useMemo(() => lennarPriceCategoryIds(tree.data?.nodes), [tree.data]);
  // The donor's island metadata is the category tree and the tiers
  // (`item_search_island/router.py`, `load-metadata`): both hold the island
  // skeleton on their first read, and either failing raises the metadata
  // error (#895).
  const tierList = useCatalogTiers();

  const itemId = active.itemId;
  const pinnedRead = useIslandItem(itemId, results);
  const pinned = pinnedRead.item;

  const [requestedId, setRequestedId] = React.useState<number | null>(null);
  const requestedRead = useIslandItem(requestedId, results);
  const requested = requestedRead.item;

  const visibleResults = results.isFetchingSearch ? [] : results.items;
  const grid = islandGridItems({
    pinned,
    results: visibleResults,
    requested,
    allResultsLoaded: settled,
  });

  const itemCount = visibleResults.length;
  // The chips' names (#895, #987): the donor's object cache of manufacturers,
  // which here is the query client's (ADR-0014 §2) — every name a read or a
  // pick carried, kept across queries and visits, so a chip does not vanish
  // when the next query's results stop carrying its manufacturer.
  const manufacturerNames = useIslandManufacturerNames();
  const rememberManufacturer = useRememberIslandManufacturer();
  const knownManufacturers = React.useMemo(
    () => [...manufacturerNames].map(([id, name]) => ({ id, name })),
    [manufacturerNames],
  );
  React.useEffect(() => {
    if (
      shouldClearEmptyCategoryFilter({
        allResultsLoaded: settled,
        itemCount,
        category: search.category ?? "",
        search: search.itemSearch ?? "",
        mfrs: search.mfrs ?? "",
        tiers: search.tiers ?? "",
        status: search.status ?? "",
      })
    ) {
      onSearchChangeRef.current({ category: undefined });
    }
  }, [
    settled,
    itemCount,
    search.category,
    search.itemSearch,
    search.mfrs,
    search.tiers,
    search.status,
  ]);

  useItemsError(results.isError || pinnedRead.isError, ITEM_SEARCH_ERROR);
  useItemsError(tree.isError || tierList.isError, ITEM_METADATA_ERROR);

  // --- The alert ----------------------------------------------------------
  const alert = islandAlert(active);
  // Closed for this row, this message and this query: the donor's alert sits
  // in the scroller, rebuilt on every query, and reopens there
  // (`InfoAlert.tsx:7-25`, `ItemSearchIsland.tsx:178-202`).
  // Any change of query forgets a close, as the rebuild does — a return to
  // the query it was closed on included.
  const queryIdentity = JSON.stringify(results.queryKey);
  const [dismissed, setDismissed] = React.useState<{
    row: ActiveRowIds | null;
    alert: string;
    query: string;
  } | null>(null);
  if (dismissed !== null && dismissed.query !== queryIdentity) setDismissed(null);
  const showAlert =
    alert !== null &&
    !(
      dismissed?.row === activeRow &&
      dismissed.alert === alert &&
      dismissed.query === queryIdentity
    );

  // --- The verbs ----------------------------------------------------------
  const scroller = React.useRef<HTMLDivElement>(null);
  const select = useSelectSpecSelectionItem();
  const remove = useClearSpecSelectionItem("card");
  const activeTarget: ItemDialogTarget | null =
    active.selectionId !== null && active.lineId !== null
      ? { lineId: active.lineId, selectionId: active.selectionId }
      : null;

  const selectItem = (
    item: Parameters<typeof selectionItemPreview>[0] & {
      category?: { name?: string; slug?: string } | null;
    },
    to: ItemDialogTarget,
    via: "card" | "dialog",
  ) => {
    const onActive = to.selectionId === activeRow?.selectionId && to.lineId === activeRow.lineId;
    select.mutate({
      ...target(),
      lineId: to.lineId,
      selectionId: to.selectionId,
      itemId: item.id,
      item: selectionItemPreview(item),
      // The card's Select hands the donor's write the Active selection as it
      // stands (`observers.ts:145`), item cleared or not; the dialog's reads
      // the selection whole from the cache (`observers.ts:159-164`).
      itemClearedAtClick: via === "card" && onActive && activeRow.itemCleared === true,
    });
    // The donor re-emits the Active selection with the item's category before
    // its write lands — only when the target is the Active selection.
    if (onActive) {
      writeCategory(itemCategorySlug(item) || active.categorySlug);
      // That re-emit carries the picked item, so a cleared item (Set as Per
      // Plan's emit) ends here. The new set is this emit, already made — its
      // category is written above — so it re-runs nothing.
      if (activeRow.itemCleared) {
        const next = { lineId: activeRow.lineId, selectionId: activeRow.selectionId };
        emitted.current = true;
        emittedFor.current = next;
        onActiveRowChange(next);
      }
    }
  };
  const removeItem = (itemIdToRemove: number, from: ItemDialogTarget, onRemoved?: () => void) => {
    // Only the target's own item (`observers.ts:301-304`).
    const row = resolveActiveRow<SpecLinesSelection, SpecLinesLine>(lines, from);
    if (row?.selection?.itemId !== itemIdToRemove) return;
    // Once the write lands, the donor's observer re-emits the Active selection
    // with no item and its stored category slug (`SpecEditIsland/data/
    // observers.ts:321-326`), so the category write runs as at activation —
    // putting back the selection's category over one picked since (#964 N14).
    // Only for the Active selection, which is the one that observer patches.
    // It re-emits the selection it captured at the click (`observers.ts:302`),
    // so a row made active while the write ran gives way to this one again.
    const reEmits = from.lineId === activeRow?.lineId && from.selectionId === activeRow.selectionId;
    const slug = row.selection.categorySlug ?? "";
    remove.mutate(
      { ...target(), lineId: from.lineId, selectionId: from.selectionId },
      {
        onSuccess: () => {
          if (reEmits) {
            const next = { lineId: from.lineId, selectionId: from.selectionId };
            emitted.current = true;
            emittedFor.current = next;
            onActiveRowChange(next);
            writeCategory(slug);
          }
          onRemoved?.();
        },
      },
    );
  };

  // Every item the island holds a copy of — the donor's object cache, as far
  // as the item dialog reads it: what is drawn now, then every copy the
  // island's cached reads hold. The donor's cache keeps what any read loaded
  // (`objectCache.ts:44-64`), so a row whose item an earlier search loaded —
  // in this visit to the editor or an earlier one — still seeds the dialog
  // (#964 N16). That memory is the query client's (ADR-0014 §2).
  const drawnItems = [
    ...grid.results,
    ...(pinned ? [pinned] : []),
    ...(requested ? [requested] : []),
  ];
  const heldItems = useIslandHeldItems();
  const knownItems = [...drawnItems, ...heldItems];

  // --- The card menu's two verbs -------------------------------------------
  const [variantFor, setVariantFor] = React.useState<number | null>(null);
  const [deleteFor, setDeleteFor] = React.useState<number | null>(null);

  const searchCardFor = (item: CatalogListItem): ItemSearchCard => ({
    isSelected: item.id === itemId,
    enableCardActions: gates.enableCardActions,
    ctaDisabled: !gates.enableCardCTAs,
    mayEditAnyItem: permissions.canBulkEditCatalogItems,
    onSelect: (shown) => {
      if (!activeTarget) return;
      selectItem(shown, activeTarget, "card");
      scroller.current?.scrollTo({ top: 0, behavior: "smooth" });
    },
    onRemove: (shown) => {
      if (activeTarget) removeItem(shown.id, activeTarget);
    },
    onCardClick: (pressedId, opening) =>
      onItemDialogChange({
        seed: seedFor(pressedId, knownItems),
        // The Active selection itself, so the dialog can tell whether its
        // target is still the one the page holds (a cleared item, below).
        target: activeTarget && activeRow,
        restoreFocus: returnsFocus(opening),
      }),
    onAddVariant: (shown) => setVariantFor(shown.id),
    onDelete: (shown) => setDeleteFor(shown.id),
    showMaxPrice,
    lennarPriceCategoryIds: lennarPriceIds,
  });

  // --- The item dialog's CTA mode -------------------------------------------
  const dialogTarget = itemDialog?.target ?? null;
  const dialogRow = resolveActiveRow<SpecLinesSelection, SpecLinesLine>(lines, dialogTarget);
  // A dialog opened on a cleared item reads none while that Active selection
  // lasts, as the donor's dialog follows the same emit
  // (`ItemDetailsIsland/data/observers.ts:93-107`).
  const dialogActive = islandActiveSelection(dialogRow, {
    itemCleared: dialogTarget !== null && dialogTarget === activeRow && activeRow.itemCleared,
  });
  const dialogCtaTarget: ItemDialogTarget | null =
    dialogTarget && dialogTarget.selectionId !== null
      ? { lineId: dialogTarget.lineId, selectionId: dialogTarget.selectionId }
      : null;
  const cta: ItemDialogCta = {
    target: dialogCtaTarget,
    selectedItemId: dialogActive.itemId,
    isFinalized: dialogActive.isFinalized,
    isPerPlan: dialogActive.isPerPlan,
    isSoftDeleted: dialogActive.isSoftDeleted,
    mayEditAnyItem: permissions.canBulkEditCatalogItems,
    onSelect: (shown, to) => selectItem(shown, to, "dialog"),
    onRemove: (shown) => {
      if (dialogCtaTarget) removeItem(shown.id, dialogCtaTarget, () => onItemDialogChange(null));
    },
  };

  // The close reads it in the commit the page's dialog state goes `null` in,
  // so the opening's choice is held past that.
  const [closeRestoresFocus, setCloseRestoresFocus] = React.useState(true);
  const opensRestoringFocus = itemDialog?.restoreFocus ?? true;
  if (itemDialog !== null && opensRestoringFocus !== closeRestoresFocus) {
    setCloseRestoresFocus(opensRestoringFocus);
  }

  const categoryId = tree.data?.nodes.find((node) => node.slug === urlCategory)?.id ?? null;

  const dialogs = (
    <>
      <ItemDetailDialog
        item={itemDialog ? withKnownItem(itemDialog.seed, knownItems) : null}
        onClose={() => onItemDialogChange(null)}
        cta={cta}
        restoreFocus={itemDialog !== null ? opensRestoringFocus : closeRestoresFocus}
      />
      <IslandAddVariant itemId={variantFor} onClose={() => setVariantFor(null)} />
      <IslandDelete itemId={deleteFor} onClose={() => setDeleteFor(null)} />
      <ErrorSnackbar open={select.isError} message={ERRORS.SELECT_ITEM} onClose={select.reset} />
      <ErrorSnackbar
        open={remove.isError}
        message={ERRORS.REMOVE_SELECTED_ITEM}
        onClose={remove.reset}
      />
    </>
  );

  // `isLoadingSearch || isLoadingMetadata`: the first load of either.
  if (
    results.isFirstLoad ||
    (tree.isPending && tree.isFetching) ||
    (tierList.isPending && tierList.isFetching)
  ) {
    return (
      <>
        <IslandSkeleton />
        {dialogs}
      </>
    );
  }

  const showSentinel =
    !results.isFetchingSearch && !results.isFetchingNextPage && !results.isError && results.hasMore;

  return (
    <>
      {/* `SearchFiltersLayout`: sticky from `lg`, `top 0`, `z 9`, the
          donor's `backgroundLight.main`, `px 24 → 32 (md)`, `pb 8`, and not
          flexing; inside it `SearchFilters`' grid, `pt 32 / pb 16`, rows 12
          apart and columns 16 apart from `sm` — the search box, then the
          filters and the applied-filter rows (#895). */}
      <div
        data-testid="item-search-filters"
        className="bg-lennar-background-light z-[9] w-full flex-none px-6 pb-2 md:px-8 lg:sticky lg:top-0"
      >
        <div className="flex flex-wrap gap-y-3 pt-8 pb-4 sm:gap-x-4">
          <div className="w-full">
            <CatalogSearchBox
              term={search.itemSearch}
              onTermChange={(term) => onSearchChange({ itemSearch: term })}
            />
          </div>
          <ItemSearchFilters
            search={search}
            onSearchChange={onSearchChange}
            knownManufacturers={knownManufacturers}
            onManufacturerPick={rememberManufacturer}
          />
        </div>
      </div>
      {/* `ItemScrollLayout`, remounted on any change of the query without its
          page, so it opens at the top: `pt 16`, `px 24 → 32`, `pb 48` from
          `md`, its own overflow, the same ground, clamped from `lg`. */}
      <div
        ref={scroller}
        key={queryIdentity}
        data-testid="item-search-scroller"
        className="bg-lennar-background-light overflow-auto px-6 pt-4 md:px-8 md:pb-12 lg:max-h-[calc(100vh-440px)] xl:max-h-[calc(100vh-380px)]"
      >
        {/* `ItemGridParentLayout`: a wrapping row, rows 16 apart, `pr 32` at
            `md` only. */}
        <div
          id="products-grid"
          data-testid="item-search-grid-parent"
          className="flex flex-wrap gap-y-4 md:pr-8 lg:pr-0"
        >
          {showAlert ? (
            <MuiAlert
              severity="info"
              data-testid="item-search-alert"
              className="mt-2 mb-4 w-full"
              onClose={() => setDismissed({ row: activeRow, alert, query: queryIdentity })}
            >
              {alert}
            </MuiAlert>
          ) : null}
          <div className="w-full">
            <ItemCardGrid
              items={[
                ...(grid.pinned ? [grid.pinned] : []),
                ...grid.results,
                ...(grid.appended ? [grid.appended] : []),
              ]}
              variantGroups={uniqueGroups([
                ...results.variantGroups,
                ...pinnedRead.variantGroups,
                ...requestedRead.variantGroups,
              ])}
              columns={ISLAND_COLUMNS}
              searchCardFor={searchCardFor}
              leading={
                itemId !== null && pinned === null && pinnedRead.isFetching ? (
                  <li className="h-full">
                    <ItemCardSkeleton testId="item-search-pinned-skeleton" />
                  </li>
                ) : null
              }
              trailing={
                <>
                  {results.isFetchingSearch || results.isFetchingNextPage ? (
                    <ItemGridSkeleton testId="item-search-grid-skeleton" asListItems />
                  ) : null}
                  {showSentinel ? (
                    <PagingSentinel onLoadMore={results.fetchNextPage} scroller={scroller} />
                  ) : null}
                </>
              }
            />
          </div>
          {settled ? (
            <RequestAddItem
              categoryId={categoryId}
              onSuccess={(result) => setRequestedId(result.itemId)}
            />
          ) : null}
          {!results.isFetchingSearch && results.isError && results.items.length === 0 ? (
            // `EmptyTable sx={{ width: '100%', mb: 4 }}` — MUI spacing 4, 32px.
            <div data-testid="item-search-no-results" className="mb-8 w-full">
              <EmptyTable text="No results" />
            </div>
          ) : null}
        </div>
      </div>
      {dialogs}
    </>
  );
}

/** The groups each once — a card's group may come from more than one read. */
function uniqueGroups(groups: IslandVariantGroups): IslandVariantGroups {
  return [...new Map(groups.map((group) => [group.id, group])).values()];
}

/**
 * Whether the item dialog's close hands the focus back to the card (#896 D1 b,
 * S5 on #903): not to a name a pointer pressed — the donor's name is a
 * paragraph that never takes the focus, so its close leaves it on the page —
 * and always to the picture, the donor's `CardActionArea`, which MUI's trap
 * focuses again (measured on local Modsy: its focus wash shows after an Escape
 * close). A keyboard opening always gets it back.
 */
function returnsFocus({ opener, pointer }: CardOpening): boolean {
  return !(opener === "name" && pointer);
}

/**
 * The island's copy of one item, as a seed carries it: the item, and its
 * gallery when a read carried one (#964 N16). The newest copy wins, as the
 * cache it is merged into keeps the newest; a tie goes to the first — the
 * drawn copy, ahead of the cache's. A copy without a gallery takes the one the
 * island holds, as the donor's merge keeps a key a later copy lacks.
 */
function knownCopy(
  id: number,
  known: readonly IslandItem[],
): Pick<ItemDialogSeed, "item" | "images"> | null {
  let item: IslandItem | undefined;
  let images: IslandItem["images"];
  for (const candidate of known) {
    if (candidate.id !== id) continue;
    if (!item || candidate.updatedAt > item.updatedAt) item = candidate;
    images ??= candidate.images;
  }
  if (!item) return null;
  images = item.images ?? images;
  return { item, ...(images ? { images } : {}) };
}

/**
 * What the dialog opens on: the pressed tile's id, and what the island knows of
 * it — the item itself included, so the dialog's header draws from the first
 * frame (#964 N16).
 */
function seedFor(id: number, known: readonly IslandItem[]): ItemDialogSeed {
  const copy = knownCopy(id, known);
  return {
    id,
    name: copy?.item?.name ?? "",
    heroImage: copy?.item?.heroImage ?? null,
    item: null,
    ...copy,
  };
}

/**
 * A seed with the island's copy of its item merged under it — the donor's row
 * partial upserted over the object cache (`ItemDetailsIsland/data/
 * observers.ts:50-64`): the row's name and hero, the cache's everything else —
 * its gallery included, which `Images.tsx` draws over the hero.
 */
function withKnownItem(seed: ItemDialogSeed, known: readonly IslandItem[]): ItemDialogSeed {
  if (seed.item) return seed;
  const copy = knownCopy(seed.id, known);
  return copy ? { ...seed, ...copy } : seed;
}

/**
 * `SearchPagination`'s sentinel: six skeleton cards that ask for the next
 * page when they come within a screen of view — `rootMargin '100% 0px'`,
 * rooted at the scroller from `lg` (where it scrolls) and at the viewport
 * below it.
 */
function PagingSentinel({
  onLoadMore,
  scroller,
}: {
  onLoadMore: () => void;
  scroller: React.RefObject<HTMLDivElement | null>;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const load = React.useRef(onLoadMore);
  React.useEffect(() => {
    load.current = onLoadMore;
  });
  // Followed across a resize: the root changes with the breakpoint.
  const lgUp = useMatchesMedia(MUI_UP_LG_QUERY);
  React.useEffect(() => {
    const target = ref.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) load.current();
      },
      { root: lgUp ? scroller.current : null, rootMargin: "100% 0px", threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [scroller, lgUp]);
  return <ItemGridSkeleton ref={ref} testId="item-search-pagination-skeleton" asListItems />;
}

/** The card menu's `Add Variant` — the catalog's dialog over the item's read. */
function IslandAddVariant({ itemId, onClose }: { itemId: number | null; onClose: () => void }) {
  const detail = useCatalogItemDetail(itemId ?? 0, { enabled: itemId !== null });
  return (
    <AddVariantDialog
      open={itemId !== null && detail.data != null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      parent={detail.data?.item ?? null}
      variants={detail.data?.variants ?? []}
      onAdded={() => {}}
    />
  );
}

/**
 * The card menu's `Delete` — `ItemDeleteIsland`'s confirmation: closed with
 * the success snackbar on success, held open with the error on failure, as the
 * item dialog's CTA mode hosts it.
 */
function IslandDelete({ itemId, onClose }: { itemId: number | null; onClose: () => void }) {
  const remove = useDeleteCatalogItem();
  useItemsError(remove.isError, DELETE_ITEM_ERROR, { onClose: () => remove.reset() });
  return (
    <ConfirmDeleteDialog
      open={itemId !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      label="item"
      isDeleting={remove.isPending}
      onConfirm={() => {
        if (itemId === null) return;
        remove.mutate(
          { itemId },
          {
            onSuccess: () => {
              onClose();
              announceItemDeleted(itemId);
            },
          },
        );
      }}
    />
  );
}
